import { driver, type DriveStep, type Side, type Alignment } from 'driver.js';
import 'driver.js/dist/driver.css';
import '../styles/tour.css';
import type { FeatureFlags, TierLimits } from '@traaker/shared';
import i18n from '../i18n';

// Shared driver.js runner for every guided tour in the app. Extracted from
// useDashboardTour when the E-Commerce feature tour landed (2026-08-10): both
// tours need the same cross-route navigation + anchor-settling machinery, which
// is where all the hard-won bug fixes live (see the comments below).
//
// driver.js renders its overlay on document.body, so it survives React Router
// navigations; we drive the route changes ourselves and wait for each
// destination page's anchor to mount before advancing.

export type TourCtx = { flags: Partial<FeatureFlags>; limits: TierLimits | null; tier: string };

export type StepDef = {
  /** Anchor selector. Omit for a centered, element-less modal — used for the
   *  welcome step AND the "what this page does" content steps, which describe a
   *  whole page rather than one widget. Centered popovers can't be detached by
   *  async layout shifts or scrolling, so they're the robust choice there. */
  element?: string;
  /** i18n key (under the `tour.` / `ecomTour.` prefix), resolved with `i18n.t()`
   *  at step-build time so the active UI language wins. */
  title: string;
  description: string;
  /** Route this anchor lives on (may carry a query string, e.g.
   *  `/settings?tab=billing`). Undefined = present on every page (sidebar /
   *  header / shared layout), so no navigation is needed to reach it. */
  route?: string;
  /** Force popover placement. Anchors near the top of the viewport (sticky
   *  header, page H4) must use side:'bottom' or driver.js renders the popover
   *  above the fold where it's invisible. */
  side?: Side;
  align?: Alignment;
  /** Pin this step's popover to the viewport center (fixed position) instead of
   *  anchoring it to the element. For full-page "what this does" steps that have
   *  no single widget to point at. */
  centered?: boolean;
  visible?: (ctx: TourCtx) => boolean;
};

// Poll for an element to appear after a route change, resolving only once the
// matched node is DIFFERENT from `stale` (the same-selector anchor still mounted
// on the route we're leaving). This is what makes cross-route navigation between
// two pages that share a `data-tour` selector reliable — we wait for the
// destination's own node to mount rather than re-resolving the departing page's.
// Resolves with whatever's matched at timeout so the tour never hangs.
function waitForFreshEl(
  selector: string,
  stale: Element | null,
  timeoutMs = 8000,
): Promise<Element | null> {
  return new Promise((resolve) => {
    const startedAt = performance.now();
    const tick = () => {
      const el = document.querySelector(selector);
      if (el && el !== stale) return resolve(el);
      if (performance.now() - startedAt > timeoutMs) return resolve(el);
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

// Poll until an element is simply PRESENT in the DOM (regardless of position).
// Distinct from waitForFreshEl (which waits for a node different from a stale
// one on cross-route nav) and waitForStable (which waits for a present node to
// stop moving). Needed for SAME-route step transitions where the destination
// page is still showing its full-page loading spinner — e.g. the centered
// "Image de marque" step → the anchored "Configurez vos prompts" step, both on
// /brand/narrative, where a freshly-onboarded brand with a running audit can
// take several seconds to render. Resolves with the element, or null at timeout.
function waitForPresent(selector: string, timeoutMs = 10000): Promise<Element | null> {
  return new Promise((resolve) => {
    const startedAt = performance.now();
    const tick = () => {
      const el = document.querySelector(selector);
      if (el) return resolve(el);
      if (performance.now() - startedAt > timeoutMs) return resolve(null);
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

// Wait until the anchor's top position stops moving. After a route change the
// page mounts the anchor early, then async content (charts, data tables) loads
// above/around it and pushes its coordinates down. driver.js positions the
// popover ONCE and never re-measures on layout shifts — so we must hold until
// the anchor settles, otherwise the popover ends up detached below the element.
// Resolves once the top is unchanged for `stableFrames` consecutive samples, or
// at `timeoutMs` (degrades gracefully rather than hanging on an animating page).
async function waitForStable(
  selector: string,
  { stableFrames = 4, timeoutMs = 4000 }: { stableFrames?: number; timeoutMs?: number } = {},
): Promise<void> {
  const startedAt = performance.now();
  let last = Number.NaN;
  let steady = 0;
  // ~60ms between samples — long enough that a single late reflow doesn't slip
  // through, short enough to feel instant once the page is quiet.
  while (performance.now() - startedAt < timeoutMs) {
    const el = document.querySelector(selector);
    const top = el ? Math.round(el.getBoundingClientRect().top) : Number.NaN;
    if (!Number.isNaN(top) && top === last) {
      if (++steady >= stableFrames) return;
    } else {
      steady = 0;
      last = top;
    }
    await sleep(60);
  }
}

// Compare the browser's current location against a step route that may carry a
// query string (e.g. `/settings?tab=billing`). Returns true when we already
// match both path and search, so we skip a redundant navigation.
export function atRoute(route: string): boolean {
  const [path, query = ''] = route.split('?');
  if (window.location.pathname !== path) return false;
  // Match only the keys the route specifies — extra params don't matter.
  if (!query) return true;
  const want = new URLSearchParams(query);
  const have = new URLSearchParams(window.location.search);
  for (const [k, v] of want) {
    if (have.get(k) !== v) return false;
  }
  return true;
}

/**
 * Filter a step list against the tenant context and drive it.
 *
 * @param onDestroyed receives `finished` = true only when the user clicked the
 *        done button on the LAST step (not an early ✕ / Échap / backdrop close),
 *        so callers can distinguish a real completion from an abandon.
 */
export function runTour(
  source: StepDef[],
  ctx: TourCtx,
  opts: { navigate: (route: string) => void; onDestroyed?: (finished: boolean) => void },
): void {
  const defs = source
    .filter((s) => (s.visible ? s.visible(ctx) : true))
    // Keep a step when: it's element-less (centered modal), it's routed (its
    // anchor is validated at navigation time), or its anchor is present now.
    // Drop only the defensive case — an unexpectedly-hidden, here-and-now
    // anchor with no route to reach it.
    .filter((s) => !s.element || s.route !== undefined || document.querySelector(s.element) !== null);

  if (defs.length === 0) return;

  // HEADER_CLEARANCE > AppBar height (64) so the anchor clears the sticky
  // header with a little breathing room.
  const HEADER_CLEARANCE = 88;

  // Scroll an anchor to a fixed offset under the sticky header. This runs
  // in driver's `onHighlighted` hook — i.e. AFTER driver has done its OWN
  // scrollIntoView({block:'center'}) on the anchor. That center-scroll is
  // the real bug on a TALL page: the button sits near the top of a long
  // document, so centering it vertically scrolls the page far DOWN, hiding
  // the button behind the header and leaving lower content (recommendations)
  // on screen. A short page had nothing to scroll, which is why it only
  // broke once "Image de marque" had results. Scrolling here — last — wins
  // over driver's center-scroll; driver's scroll listener then re-measures
  // and repositions the side:'bottom' popover onto the correctly-placed
  // anchor. Sidebar nav items live in a fixed drawer (window scroll doesn't
  // move them) so they're skipped.
  const scrollAnchorUnderHeader = (element?: string) => {
    if (!element || element.includes('nav:')) return;
    const target = document.querySelector(element);
    if (!target) return;
    const top = target.getBoundingClientRect().top + window.scrollY - HEADER_CLEARANCE;
    window.scrollTo({ top: Math.max(0, top) });
  };

  // Before driver highlights an anchored step, ensure the anchor has MOUNTED
  // and its position has SETTLED. On a same-route step transition (the
  // centered "Image de marque" step → the anchored "Configurez vos prompts"
  // step, both on /brand/narrative) goTo never runs waitForFreshEl, and the
  // page can still be showing its full-page loading spinner for several
  // seconds (freshly-onboarded brand + running audit) — so the button
  // hasn't rendered. Without this wait driver finds no anchor and falls
  // back to its center-screen dummy element (popover centered, button
  // hidden). Actual scrolling is deferred to scrollAnchorUnderHeader in
  // onHighlighted (see above).
  //
  // Both waits are a POSITIONING concern only, so a `centered` step skips them
  // for a short grace window instead: its popover is CSS-pinned to the viewport
  // whether or not the anchor ever mounts, and the full waits turn "Suivant"
  // into a multi-second freeze when the anchor is absent by design. The
  // E-Commerce tour makes every anchored step centered, and on a tenant with no
  // catalogue several of its anchors never render at all (`product-opt-
  // distribution` is gated on `stats.total > 0`, `product-content-sources` on
  // `catalogues.length > 0`) — a 10s waitForPresent + 4s waitForStable, plus 8s
  // of waitForFreshEl on a cross-route step, stalled steps 7 and 13 for 14s and
  // 22s (fixed 2026-08-11). The grace window is long enough that a fast anchor
  // still gets its stage cutout, short enough to read as instant when it's gone.
  const CENTERED_ANCHOR_GRACE_MS = 500;

  const positionAnchor = async (element?: string, centered?: boolean) => {
    if (!element || element.includes('nav:')) return;
    if (centered) {
      await waitForPresent(element, CENTERED_ANCHOR_GRACE_MS);
      return;
    }
    const present = await waitForPresent(element);
    if (!present) return;
    // Wait for it to stop moving as async content (charts, matrix) loads
    // above it and pushes its coordinates down.
    await waitForStable(element);
  };

  // Cross-route navigation: when the next step lives on a different route,
  // navigate there and wait for its FRESH anchor (several pages share the
  // `page-intro` / `schedule-control` selectors — the departing page's node
  // is still mounted at navigate() time, so we must wait for a new node).
  const goTo = async (route: string | undefined, element?: string, centered?: boolean) => {
    if (route && !atRoute(route)) {
      const stale = element ? document.querySelector(element) : null;
      opts.navigate(route);
      window.scrollTo({ top: 0 });
      // Element-less (centered) steps have no anchor to await; give the new
      // route a beat to mount so the popover shows over the right page. A
      // centered step WITH an anchor gets the same short beat (see
      // CENTERED_ANCHOR_GRACE_MS) rather than the full 8s stale-node wait.
      if (element && !centered) await waitForFreshEl(element, stale);
      else await sleep(150);
    }
    // Always position the anchor — covers same-route steps too (e.g.
    // page-intro → prompt-config on the same axis page).
    await positionAnchor(element, centered);
  };

  // Set true when the user legitimately finishes the LAST step ("Terminer")
  // so onDestroyStarted can skip the confirm only in that case.
  let finishing = false;
  const lastIdx = defs.length - 1;

  const steps: DriveStep[] = defs.map((s, i) => {
    const next = defs[i + 1];
    const prev = defs[i - 1];
    const step: DriveStep = {
      element: s.element,
      popover: {
        title: i18n.t(s.title),
        description: i18n.t(s.description),
        ...(s.side ? { side: s.side } : {}),
        ...(s.align ? { align: s.align } : {}),
        // Per-step popoverClass: the centered variant pins to the viewport
        // center; everything else keeps the default element-anchored look.
        popoverClass: s.centered ? 'traaker-tour traaker-tour-centered' : 'traaker-tour',
      },
    };
    step.popover!.onNextClick = async () => {
      if (i === lastIdx) {
        finishing = true;
        d.destroy();
        return;
      }
      await goTo(next?.route, next?.element, next?.centered);
      d.moveNext();
    };
    step.popover!.onPrevClick = async () => {
      await goTo(prev?.route, prev?.element, prev?.centered);
      d.movePrevious();
    };
    return step;
  });

  const d = driver({
    showProgress: true,
    allowClose: true,
    smoothScroll: false,
    // No transition animation: during driver's 400ms highlight tween,
    // `__activeElement` still points at the PREVIOUS step's anchor. A scroll
    // /resize fired by the new page loading its charts re-measures that
    // stale (now-unmounted) anchor and flings the popover to the viewport
    // edge — the "Image de marque popover jumps to the bottom after the page
    // loads" bug. Instant transitions close that window.
    animate: false,
    overlayColor: 'rgba(11, 11, 15, 0.55)',
    stagePadding: 6,
    stageRadius: 12,
    popoverClass: 'traaker-tour',
    nextBtnText: i18n.t('tour.btn.next'),
    prevBtnText: i18n.t('tour.btn.prev'),
    doneBtnText: i18n.t('tour.btn.done'),
    progressText: '{{current}} / {{total}}',
    steps,
    // After driver highlights a step, scroll its anchor under the sticky
    // header so it clears the header and (for CENTERED steps) sits above the
    // viewport-pinned popover instead of behind it. Skipped only for
    // nav-drawer anchors (fixed, window scroll doesn't move them) and
    // element-less steps.
    //   - Anchored (side-placed) steps ALSO need d.refresh() so driver
    //     re-runs its placement against the re-scrolled anchor — without it
    //     a ~380px popover positioned against a vertically-centered button
    //     falls back to bottom:10px (off-screen).
    //   - Centered steps must NOT refresh: the popover is CSS-pinned to the
    //     viewport center regardless, and skipping refresh avoids a needless
    //     reflow.
    onHighlighted: (el) => {
      const active = defs[d.getActiveIndex() ?? -1];
      if (active?.element && !active.element.includes('nav:')) {
        scrollAnchorUnderHeader(active.element);
        if (!active.centered) d.refresh();
      }
      void el;
    },
    // Confirm before an accidental exit (backdrop click / ✕ / Échap). A
    // legitimate finish on the last step sets `finishing` and skips it.
    onDestroyStarted: () => {
      if (!finishing && d.hasNextStep()) {
        const ok = window.confirm(i18n.t('tour.exitConfirm'));
        if (!ok) return; // abort teardown — stay on the current step
      }
      d.destroy();
    },
    onDestroyed: () => opts.onDestroyed?.(finishing),
  });
  d.drive();
}
